import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaEdit, FaTrashAlt, FaPlus } from 'react-icons/fa';
import { getBaseUrl } from "../global/Globalapi";
const AccountType = () => {
  // API Base URL
  // const API_BASE_URL = 'http://localhost:5000';
const API_BASE_URL = getBaseUrl();
  // State for form data - matching database column names
  const [formData, setFormData] = useState({
    acc_grp_name: '',
    description: '',
    level: 'Secondary',
    parent_grp_id_id: '',
    iseditable: 'Yes'
  });

  // State for account types list
  const [accountTypes, setAccountTypes] = useState([]);
  const [primaryGroup, setPrimaryGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editId, setEditId] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Fetch initial data
  useEffect(() => {
    fetchAccountTypes();
    fetchPrimaryGroup();
  }, []);

  // Fetch all account types
  const fetchAccountTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/accounttype`);
      setAccountTypes(response.data);
    } catch (error) {
      console.error('Error fetching account types:', error);
      setMessage({ type: 'error', text: 'Error fetching account types' });
    }
  };

  // Fetch primary account group
  const fetchPrimaryGroup = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/accounttype/primary`);
      setPrimaryGroup(response.data);
    } catch (error) {
      console.log('No primary group found, will create on first submission');
      setPrimaryGroup(null);
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If level is changed to Primary, clear parent
    if (name === 'level' && value === 'Primary') {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        parent_grp_id_id: '',
        iseditable: 'No' // Primary groups are not editable by default
      }));
    } else if (name === 'level' && value === 'Secondary') {
      // If level is Secondary, set parent to primary if exists
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        parent_grp_id_id: primaryGroup ? primaryGroup.acc_type_id : '',
        iseditable: 'Yes'
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const submitData = { ...formData };
      
      // If Secondary and no parent selected, the backend will auto-assign Primary as parent
      if (submitData.level === 'Secondary' && !submitData.parent_grp_id_id) {
        submitData.parent_grp_id_id = primaryGroup ? primaryGroup.acc_type_id : null;
      }
      
      // If Primary, ensure no parent
      if (submitData.level === 'Primary') {
        submitData.parent_grp_id_id = null;
      }

      if (editId) {
        // Update existing account type
        await axios.put(`${API_BASE_URL}/api/accounttype/${editId}`, submitData, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000
        });
        setMessage({ type: 'success', text: 'Account group updated successfully!' });
      } else {
        // Create new account type
        await axios.post(`${API_BASE_URL}/api/accounttype`, submitData, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000
        });
        setMessage({ type: 'success', text: 'Account group created successfully!' });
      }
      
      fetchAccountTypes();
      fetchPrimaryGroup();
      handleReset();
    } catch (error) {
      console.error('Error saving account type:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Error saving account group' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (acc_type_id) => {
    if (!window.confirm('Are you sure you want to delete this account group?')) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/api/accounttype/${acc_type_id}`);
      setMessage({ type: 'success', text: 'Account group deleted successfully!' });
      fetchAccountTypes();
      fetchPrimaryGroup();
    } catch (error) {
      console.error('Error deleting account type:', error);
      const errorMsg = error.response?.data?.message || 'Error deleting account group';
      setMessage({ type: 'error', text: errorMsg });
    }
  };

  // Reset form
  const handleReset = () => {
    setEditId(null);
    setFormData({
      acc_grp_name: '',
      description: '',
      level: 'Secondary',
      parent_grp_id_id: primaryGroup ? primaryGroup.acc_type_id : '',
      iseditable: 'Yes'
    });
  };

  // Handle edit
  const handleEdit = (accountType) => {
    setEditId(accountType.acc_type_id);
    setFormData({
      acc_grp_name: accountType.acc_grp_name || '',
      description: accountType.description || '',
      level: accountType.level || 'Secondary',
      parent_grp_id_id: accountType.parent_grp_id_id || '',
      iseditable: accountType.iseditable || 'Yes'
    });
    
    window.scrollTo({ top: document.querySelector('.form-section')?.offsetTop || 0, behavior: 'smooth' });
  };

  // Get parent options (exclude self and children to prevent circular reference)
  const getParentOptions = () => {
    return accountTypes.filter(type => {
      // Can't select self as parent
      if (editId && type.acc_type_id === editId) return false;
      // Can't select own children (would need recursive check for deep hierarchies)
      if (type.parent_grp_id_id === editId) return false;
      return true;
    });
  };

  // Pagination calculations
  const totalPages = Math.ceil(accountTypes.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAccountTypes = accountTypes.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    margin: '8px 0 15px 0',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '15px',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    fontWeight: '600',
    display: 'block',
    marginBottom: '5px',
    color: '#555',
    fontSize: '14px'
  };

  const requiredStar = {
    color: 'red',
    marginLeft: '4px'
  };

  const paginationButtonStyle = {
    padding: '8px 16px',
    margin: '0 5px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '50px auto', padding: '20px' }}>
      {/* List Section */}
      <div style={{
        padding: '30px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        backgroundColor: '#fff',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        marginBottom: '30px'
      }}>
        <h3 style={{ 
          marginBottom: '20px', 
          color: '#333',
          borderBottom: '2px solid #2196F3',
          paddingBottom: '10px'
        }}>
          Account Groups List
        </h3>

        {message.text && (
          <div style={{
            padding: '12px',
            marginBottom: '20px',
            borderRadius: '4px',
            backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
            color: message.type === 'success' ? '#155724' : '#721c24',
            border: message.type === 'success' ? '1px solid #c3e6cb' : '1px solid #f5c6cb',
            fontSize: '14px'
          }}>
            {message.text}
          </div>
        )}

        {accountTypes.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
            <p>No account groups found.</p>
            <p style={{ fontSize: '14px', marginTop: '10px' }}>
              Create a "Primary" account group first, then add secondary groups under it.
            </p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#b91c1c', color: 'white' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>S.No</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Group Name</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Description</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Level</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Parent Group</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Editable</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentAccountTypes.map((type, index) => (
                    <tr key={type.acc_type_id} style={{ 
                      backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff',
                      borderBottom: '1px solid #ddd'
                    }}>
                      <td style={{ padding: '12px' }}>{indexOfFirstItem + index + 1}</td>
                      <td style={{ padding: '12px', fontWeight: type.level === 'Primary' ? '600' : 'normal' }}>
                        {type.level === 'Primary' && '⭐ '}
                        {type.acc_grp_name}
                      </td>
                      <td style={{ padding: '12px' }}>{type.description || '-'}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: type.level === 'Primary' ? '#fff3cd' : '#e7f3ff',
                          color: type.level === 'Primary' ? '#856404' : '#0066cc'
                        }}>
                          {type.level}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>{type.parent_name || '-'}</td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: type.iseditable === 'Yes' ? '#d4edda' : '#f8d7da',
                          color: type.iseditable === 'Yes' ? '#155724' : '#721c24'
                        }}>
                          {type.iseditable}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleEdit(type)}
                          style={{
                            backgroundColor: '#2196F3',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '8px'
                          }}
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(type.acc_type_id)}
                          style={{
                            backgroundColor: '#f44336',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          title="Delete"
                        >
                          <FaTrashAlt />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                marginTop: '20px',
                gap: '10px'
              }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    ...paginationButtonStyle,
                    backgroundColor: currentPage === 1 ? '#ccc' : '#b91c1c',
                    color: 'white',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>
                
                <div style={{ display: 'flex', gap: '5px' }}>
                  {[...Array(totalPages)].map((_, idx) => {
                    const page = idx + 1;
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          style={{
                            ...paginationButtonStyle,
                            backgroundColor: currentPage === page ? '#b91c1c' : '#e0e0e0',
                            color: currentPage === page ? 'white' : '#333'
                          }}
                        >
                          {page}
                        </button>
                      );
                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} style={{ padding: '8px' }}>...</span>;
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    ...paginationButtonStyle,
                    backgroundColor: currentPage === totalPages ? '#ccc' : '#b91c1c',
                    color: 'white',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
              </div>
            )}

            <div style={{ textAlign: 'center', marginTop: '10px', color: '#666', fontSize: '14px' }}>
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, accountTypes.length)} of {accountTypes.length} entries
            </div>
          </>
        )}
      </div>

      {/* Form Section */}
      <div className="form-section" style={{
        padding: '30px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        backgroundColor: '#f9f9f9',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          marginBottom: '30px', 
          color: '#333',
          borderBottom: '2px solid #4CAF50',
          paddingBottom: '10px'
        }}>
          {editId ? 'Edit Account Group' : 'Add Account Group'}
        </h2>

        {!primaryGroup && !editId && (
          <div style={{
            padding: '15px',
            marginBottom: '20px',
            borderRadius: '4px',
            backgroundColor: '#fff3cd',
            color: '#856404',
            border: '1px solid #ffc107',
            fontSize: '14px'
          }}>
            <strong>Note:</strong> No Primary account group exists. The first group you create with "Primary" level will become the default parent for all other groups.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
            {/* Group Name */}
            <div>
              <label style={labelStyle}>
                Group Name <span style={requiredStar}>*</span>
              </label>
              <input
                type="text"
                name="acc_grp_name"
                value={formData.acc_grp_name}
                onChange={handleChange}
                style={inputStyle}
                placeholder="Enter Group Name"
                required
              />
            </div>

            {/* Level */}
            <div>
              <label style={labelStyle}>
                Level <span style={requiredStar}>*</span>
              </label>
              <select
                name="level"
                value={formData.level}
                onChange={handleChange}
                style={inputStyle}
                required
                disabled={editId && accountTypes.find(t => t.acc_type_id === editId)?.level === 'Primary'}
              >
                <option value="Primary">Primary (Root Group)</option>
                <option value="Secondary">Secondary</option>
              </select>
              {formData.level === 'Primary' && (
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Primary groups have no parent and serve as root categories.
                </small>
              )}
            </div>

            {/* Parent Group - Only show for non-primary */}
            {formData.level !== 'Primary' && (
              <div>
                <label style={labelStyle}>
                  Parent Group
                </label>
                <select
                  name="parent_grp_id_id"
                  value={formData.parent_grp_id_id}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="">
                    {primaryGroup ? `Default: ${primaryGroup.acc_grp_name}` : 'Select Parent Group'}
                  </option>
                  {getParentOptions().map(type => (
                    <option key={type.acc_type_id} value={type.acc_type_id}>
                      {type.level === 'Primary' ? '⭐ ' : ''}{type.acc_grp_name}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#666', fontSize: '12px' }}>
                  If not selected, defaults to Primary group as parent.
                </small>
              </div>
            )}

            {/* Is Editable */}
            <div>
              <label style={labelStyle}>
                Is Editable
              </label>
              <select
                name="iseditable"
                value={formData.iseditable}
                onChange={handleChange}
                style={inputStyle}
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            {/* Description - Full width */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                placeholder="Enter description (optional)"
              />
            </div>
          </div>

          {/* Form Buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '15px', 
            marginTop: '25px' 
          }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 30px',
                backgroundColor: loading ? '#ccc' : '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {loading ? 'Saving...' : (editId ? 'Update' : <><FaPlus /> Add Group</>)}
            </button>
            <button
              type="button"
              onClick={handleReset}
              style={{
                padding: '12px 30px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountType;
